import { DATE_FORMAT_STRING, MS_PER_DAY, TDate, TTime, TTimelineDateRangeDTO, TUnitOfTime } from '@monorepo/types';
import moment from 'moment';

export const DISPLAY_PIPE_DATE_FORMAT = 'dd-MM-yyyy';

export class DateUtils {
  //example: Tue 19 Dec 2023
  static fullDateFormat() {
    const day = moment();
    return `${day.format('ddd')} ${day.format('DD')} ${day.format('MMM')} ${day.format('YYYY')}`;
  }

  //example 13:45 -> 1:45pm
  static getTimeformatte(time: string) {
    const parsedTime = moment(time, 'HH:mm');
    const formattedTime = parsedTime.format('h:mma');
    return formattedTime;
  }

  // get current date //
  static getCrntDate(): TDate {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}` as TDate;
  }

  static getCrntTime() {
    return new Date().toTimeString().slice(0, 5) as TTime;
  }
  static getMinsFromTime(time: TTime) {
    return Number(time.slice(0, 2)) * 60 + Number(time.slice(3, 5));
  }

  static getTimeFromMins(mins: number) {
    return `${String(Math.floor(mins / 60) % 24).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}` as TTime;
  }
  static getTimeFromEpoch(epoch: number, timeZone?: string) {
    return new Intl.DateTimeFormat('en-GB', { timeZone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(
      epoch,
    ) as TTime;
  }

  //Here datestring is of format YYYY-MM-DD as sent by backend
  static getDateFromDateString = (dateString: string) => {
    return new Date(dateString);
  };

  //Here datestring is of format YYYY-MM-DD as sent by backend
  static getDateStringFromDate = (date: number | Date | string): TDate => {
    return moment(date).format('YYYY-MM-DD') as TDate;
  };

  static getDisplayDateStringFromDate = (date: number | Date | string) => {
    return moment(date).format('DD-MM-YYYY');
  };

  static getFirstDateOfMonthFromDate = (date: Date): TDate => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return this.getDateStringFromDate(new Date(year, month).valueOf());
  };

  static getLastDateOfMonthFromDate = (date: number | Date): TDate => {
    const date$ = new Date(date);
    const year = date$.getFullYear();
    const month = date$.getMonth();
    return this.getDateStringFromDate(new Date(year, month + 1, 0).valueOf());
  };

  //takes two YYYY-MM-DD strings and returns one between them YYYY-MM-DD
  static generateRandomDate = (startDate: string, endDate: string): string => {
    const firstDate = new Date(startDate);
    const lastDate = new Date(endDate);
    const timeDiff = lastDate.getTime() - firstDate.getTime();
    const randomTime = Math.random() * timeDiff;
    const randomDate = new Date(firstDate.getTime() + randomTime);
    return DateUtils.getDateStringFromDate(randomDate);
  };

  /**
   * Checks whether a given date string falls within the past N days (inclusive).
   *
   * @param date - The input date string (ISO format or any format parsable by Moment).
   * @param numOfDays - The number of days to look back from today.
   * @returns `true` if the date is within the past `numOfDays` (including today), `false` otherwise.
   *
   * @example
   * isDateWithinPastDays('2025-07-01', 3) // returns true if today is between July 1 and July 3, 2025
   */
  static isDateWithinPastDays(date: string, numOfDays: number): boolean {
    const today = moment();
    const selected = moment.parseZone(date);
    const fromDate = today.clone().subtract(numOfDays, 'days');

    return selected.isSameOrAfter(fromDate, 'day') && selected.isSameOrBefore(today, 'day');
  }

  /**
   * Fits a requested date range within an available date range, preserving the requested span whenever possible.
   *
   * @param requested - The desired date range.
   * @param available - The date range that the result must fit within.
   * @param anchorDate - Optional date that the returned range should include when the requested range has no overlap with the available range. Defaults to today.
   *
   * Rules:
   * - If the requested range already fits entirely within the available range,
   *   it is returned unchanged.
   * - If the requested range is wider than the available range, the available
   *   range is returned.
   * - If the requested range partially overlaps the available range, the overlap
   *   is preserved and the range is extended on the clipped side to maintain the
   *   original width.
   * - If there is no overlap:
   *   - If `anchorDate` (or today when omitted) lies within the available range,
   *     the returned range is anchored at that date while preserving the original
   *     width. If necessary, it is shifted so it remains entirely within the
   *     available range while still containing the anchor date.
   *   - Otherwise, the returned range starts from the beginning of the available
   *     range.
   *
   * All dates are required to be in YYYY-MM-DD format and are treated as whole days (time components are ignored).
   *
   * @example
   * ```ts
   * fitDateRange(
   *   { from: '2026-01-05', to: '2026-01-10' },
   *   { from: '2026-01-01', to: '2026-01-31' }
   * )
   * // => { from: '2026-01-05', to: '2026-01-10' }
   * ```
   *
   * @example
   * ```ts
   * fitDateRange(
   *   { from: '2026-01-01', to: '2026-01-15' },
   *   { from: '2026-01-10', to: '2026-01-31' }
   * )
   * // => { from: '2026-01-10', to: '2026-01-24' }
   *```
   *
   * @example
   * ```ts
   * fitDateRange(
   *   { from: '2026-03-01', to: '2026-03-10' },
   *   { from: '2026-01-01', to: '2026-01-31' },
   *   '2026-01-28'
   * )
   * // => { from: '2026-01-22', to: '2026-01-31' }
   * ```
   */
  static fitDateRange(
    requested: TTimelineDateRangeDTO,
    available: TTimelineDateRangeDTO,
    anchorDate?: string,
  ): TTimelineDateRangeDTO {
    const requestedWidth = (Date.parse(requested.to) - Date.parse(requested.from)) / MS_PER_DAY + 1;
    const availableWidth = (Date.parse(available.to) - Date.parse(available.from)) / MS_PER_DAY + 1;

    // Available range is narrower than requested.
    if (availableWidth <= requestedWidth) return available;

    // Requested already fits completely.
    if (requested.from >= available.from && requested.to <= available.to) return requested;

    const hasOverlap = requested.from <= available.to && requested.to >= available.from;

    // --------------------------------------------------------------------------
    // Partial overlap.
    // --------------------------------------------------------------------------
    if (hasOverlap) {
      let from = moment(requested.from > available.from ? requested.from : available.from, DATE_FORMAT_STRING).startOf(
        'day',
      );

      let to = moment(requested.to < available.to ? requested.to : available.to, DATE_FORMAT_STRING).startOf('day');

      const overlapWidth = to.diff(from, 'days') + 1;
      const remaining = requestedWidth - overlapWidth;

      if (requested.from < available.from) {
        // Left side was clipped -> extend right.
        to.add(remaining, 'days');
      } else {
        // Right side was clipped -> extend left.
        from.subtract(remaining, 'days');
      }

      return { from: from.format(DATE_FORMAT_STRING), to: to.format(DATE_FORMAT_STRING) };
    }

    // --------------------------------------------------------------------------
    // No overlap.
    // --------------------------------------------------------------------------
    const availableFrom = moment(available.from, DATE_FORMAT_STRING).startOf('day');
    const availableTo = moment(available.to, DATE_FORMAT_STRING).startOf('day');

    const anchor = (anchorDate ? moment(anchorDate, DATE_FORMAT_STRING) : moment()).startOf('day');

    // Anchor lies within the available range.
    if (!anchor.isBefore(availableFrom, 'day') && !anchor.isAfter(availableTo, 'day')) {
      let from = anchor.clone();
      let to = anchor.clone().add(requestedWidth - 1, 'days');

      // Doesn't fit -> shift left while keeping anchor included.
      if (to.isAfter(availableTo, 'day')) {
        const shift = to.diff(availableTo, 'days');
        to = availableTo;
        from.subtract(shift, 'days');
      }

      return { from: from.format(DATE_FORMAT_STRING), to: to.format(DATE_FORMAT_STRING) };
    }

    // Anchor is outside the available range.
    return {
      from: availableFrom.format(DATE_FORMAT_STRING),
      to: availableFrom
        .clone()
        .add(requestedWidth - 1, 'days')
        .format(DATE_FORMAT_STRING),
    };
  }

  static dateRangeDiff(range: TTimelineDateRangeDTO, unitOfTime: TUnitOfTime) {
    const from = new Date(range.from);
    const to = new Date(range.to);

    switch (unitOfTime) {
      case 'days':
        return (to.getTime() - from.getTime()) / MS_PER_DAY;

      case 'months':
        return (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth());
    }
  }

  static addToDate(date: string, unitOfTime: TUnitOfTime, amount: number): TDate {
    const result = new Date(`${date.slice(0, 10)}T00:00:00.000Z`);

    switch (unitOfTime) {
      case 'days':
        result.setUTCDate(result.getUTCDate() + amount);
        break;

      // Clamps to the last day of the target month instead of overflowing into the
      // next one, e.g. 2026-01-31 + 1 month => 2026-02-28 (not 2026-03-03).
      case 'months': {
        const dayOfMonth = result.getUTCDate();
        result.setUTCDate(1);
        result.setUTCMonth(result.getUTCMonth() + amount);
        const lastDayOfTargetMonth = new Date(
          Date.UTC(result.getUTCFullYear(), result.getUTCMonth() + 1, 0),
        ).getUTCDate();
        result.setUTCDate(Math.min(dayOfMonth, lastDayOfTargetMonth));
        break;
      }
    }

    return result.toISOString().slice(0, 10) as TDate;
  }

  static subtractFromDate(date: TDate, unitOfTime: TUnitOfTime, amount: number): TDate {
    return DateUtils.addToDate(date, unitOfTime, -amount);
  }
}
